import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import { getBlogArticle, listBlogArticles, type BlogListItem } from "../lib/blog.functions";
import { ShareButtons } from "../components/share-buttons";
import { BlogSidebar } from "../components/blog-sidebar";
import { BlogServiceLinks } from "../components/blog-service-links";
import { CallLabel } from "../components/call-label";
import { openQuoteModal } from "../components/quote-modal";
import { heroSrc } from "../lib/blog-image";
import {
  type BlogLocale,
  type BlogLocaleCode,
  SUPPORTED_LOCALES,
  getLocale,
  localeFromSlug,
  scriptFontLinks,
} from "../lib/blog-locales";

const SITE = "https://capitalpropressurewashing.com";

export const Route = createFileRoute("/blog/$slug")({
  loader: async ({ params }) => {
    const article = await getBlogArticle({ data: { slug: params.slug } });
    if (!article) throw notFound();
    let related: BlogListItem[] = [];
    try {
      const lang = localeFromSlug(params.slug);
      const all = await listBlogArticles({ data: { language: lang } });
      related = all.filter((a) => a.slug !== params.slug).slice(0, 6);
    } catch {
      related = [];
    }
    return { article, related };
  },
  head: ({ params, loaderData }) => {
    const url = `${SITE}/blog/${params.slug}`;
    if (!loaderData) {
      return {
        meta: [
          { title: "Article Not Found | Capital Pro Pressure Washing" },
          { name: "robots", content: "noindex" },
        ],
      };
    }
    const a = loaderData.article;
    const locale = getLocale(localeFromSlug(params.slug));
    // Keep titles in the 45-60 char sweet spot: use the article title as-is
    // when it already qualifies, only appending the brand when it's too short.
    const title =
      a.title.length >= 45 && a.title.length <= 60
        ? a.title
        : `${a.title} | Capital Pro Pressure Washing`;
    const description = a.meta_description ?? a.excerpt ?? "";
    const rawImage = a.hero_image_url ?? null;
    // Localized heroes are served from a relative API path; social crawlers
    // need an absolute URL.
    const image = rawImage?.startsWith("/") ? `${SITE}${rawImage}` : rawImage;

    // Build hreflang alternate links for every live translation.
    const altLinks = a.alternates
      .map((alt) => ({
        rel: "alternate" as const,
        hrefLang: alt.locale,
        href: `${SITE}/blog/${alt.slug}`,
      }))
      .sort((x, y) => x.hrefLang.localeCompare(y.hrefLang));
    const defaultAlt = a.alternates.find(
      (alt) => alt.locale === "en",
    );
    if (defaultAlt) {
      altLinks.push({
        rel: "alternate",
        hrefLang: "x-default",
        href: `${SITE}/blog/${defaultAlt.slug}`,
      });
    }

    const scripts: Array<{ type: string; children: string }> = [];
    if (a.json_ld) {
      scripts.push({ type: "application/ld+json", children: a.json_ld });
    } else {
      scripts.push({
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BlogPosting",
          headline: a.title,
          description,
          datePublished: a.created_at,
          dateModified: a.updated_at ?? a.created_at,
          mainEntityOfPage: url,
          inLanguage: locale.htmlLang,
          ...(image ? { image: [image] } : {}),
          author: {
            "@type": "Organization",
            name: "Capital Pro Pressure Washing",
            url: SITE,
          },
          publisher: {
            "@type": "Organization",
            name: "Capital Pro Pressure Washing",
            url: SITE,
          },
        }),
      });
    }
    if (a.faq_json_ld) {
      scripts.push({ type: "application/ld+json", children: a.faq_json_ld });
    }
    scripts.push({
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: locale.ui.homeLabel,
            item: `${SITE}/`,
          },
          {
            "@type": "ListItem",
            position: 2,
            name: locale.ui.blogTitle,
            item: `${SITE}${locale.indexRoute}`,
          },
          { "@type": "ListItem", position: 3, name: a.title, item: url },
        ],
      }),
    });

    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "article" },
        { property: "og:url", content: url },
        { property: "og:locale", content: locale.ogLocale },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
        ...(image
          ? [
              { property: "og:image", content: image },
              { name: "twitter:image", content: image },
            ]
          : []),
      ],
      links: [
        { rel: "canonical", href: url },
        ...altLinks,
        ...scriptFontLinks(locale.code),
      ],
      scripts,
    };
  },
  component: ArticlePage,
  errorComponent: () => <ArticleMissing message="We couldn't load this article." />,
  notFoundComponent: () => (
    <ArticleMissing message="That article doesn't exist or has been moved." />
  ),
});

function ArticleMissing({ message }: { message: string }) {
  return (
    <main className="mx-auto max-w-[720px] px-4 py-20 text-center">
      <p className="font-[Oswald,sans-serif] text-6xl font-bold text-[var(--brand-deep-blue)]">
        404
      </p>
      <h1 className="mt-4 font-[Oswald,sans-serif] text-2xl font-bold uppercase text-[var(--brand-deep-blue)]">
        Article not found
      </h1>
      <p className="mt-3 text-[var(--brand-charcoal)]/75">{message}</p>
      <Link
        to="/blog"
        className="mt-6 inline-block rounded-md bg-[var(--brand-red)] px-6 py-3 font-bold text-white"
      >
        Back to the blog
      </Link>
    </main>
  );
}

function formatDate(value: string, locale: BlogLocale): string {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  try {
    return d.toLocaleDateString(locale.dateLocale, {
      year: "numeric",
      month: "long",
      day: "numeric",
      timeZone: "UTC",
    });
  } catch {
    return d.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      timeZone: "UTC",
    });
  }
}

/**
 * The synced CMS HTML repeats the title as an <h1> and the hero image as the
 * first block. We render both ourselves, so strip them from the body.
 */
function cleanContent(html: string, heroUrl: string | null): string {
  // Inline JSON-LD blocks are rendered from structured fields in <head>; keeping
  // them in the body also breaks hydration.
  let out = html.replace(/<script\b[\s\S]*?<\/script>\s*/gi, "");
  out = out.replace(/<noscript\b[\s\S]*?<\/noscript>\s*/gi, "");
  out = out.replace(/<h1\b[^>]*>[\s\S]*?<\/h1>\s*/i, "");
  // The CMS labels the summary callout "TL;DR" — use plainer wording.
  out = out.replace(/TL\s*[;:,]?\s*DR\s*:?/gi, "Quick Summary:");
  if (heroUrl) {
    const escaped = heroUrl.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    out = out.replace(
      new RegExp(`<p>\\s*<img[^>]*src=["']${escaped}["'][^>]*>\\s*</p>\\s*`, "i"),
      "",
    );
    out = out.replace(
      new RegExp(`<img[^>]*src=["']${escaped}["'][^>]*>\\s*`, "i"),
      "",
    );
  }
  // Translated articles carry a localized hero URL, so the original English
  // title-card image still sits at the top of the body. Drop any leading image.
  out = out
    .replace(/^\s*<p>\s*<img[^>]*>\s*<\/p>\s*/i, "")
    .replace(/^\s*<figure\b[^>]*>[\s\S]*?<\/figure>\s*/i, "")
    .replace(/^\s*<img[^>]*>\s*/i, "");
  return out.replace(/^\s*<hr\s*\/?>\s*/i, "").trim();
}

function ArticlePage() {
  const { article, related } = Route.useLoaderData();
  const { slug } = Route.useParams();
  const shareUrl = `${SITE}/blog/${slug}`;
  const locale = getLocale(localeFromSlug(slug));
  const ui = locale.ui;
  const isRtl = locale.dir === "rtl";

  const body = article.content_html
    ? cleanContent(article.content_html, article.hero_image_url ?? null)
    : "";
  const minutes = article.read_minutes;

  // Build the language switcher from live alternates.
  const otherLocales = article.alternates.filter(
    (alt: { locale: string; slug: string }) => alt.slug !== slug,
  );

  return (
    <main lang={locale.htmlLang} dir={locale.dir} className="overflow-x-clip">
      <nav
        aria-label="Breadcrumb"
        className="border-b border-gray-200 bg-[var(--brand-light-gray)]"
      >
        <div
          className={`mx-auto flex max-w-[860px] items-center gap-2 px-4 py-3 text-sm text-[var(--brand-charcoal)]/70 ${isRtl ? "flex-row-reverse" : ""}`}
        >
          <Link to="/" className="hover:text-[var(--brand-deep-blue)]">
            {ui.homeLabel}
          </Link>
          <span>/</span>
          <Link
            to={locale.indexRoute as "/blog" | "/es/blog" | "/zh/blog" | "/ur/blog" | "/ar/blog"}
            className="hover:text-[var(--brand-deep-blue)]"
          >
            {ui.blogTitle}
          </Link>
          {otherLocales.length > 0 ? (
            <div className={`ml-auto flex flex-wrap gap-1.5 ${isRtl ? "ml-0 mr-auto" : ""}`}>
              {otherLocales.map((alt: { locale: string; slug: string }) => {
                const altLocale = getLocale(alt.locale as BlogLocaleCode);
                return (
                  <Link
                    key={alt.locale}
                    to="/blog/$slug"
                    params={{ slug: alt.slug }}
                    hrefLang={alt.locale}
                    className="rounded-md border border-gray-300 bg-white px-2.5 py-1 text-xs font-semibold text-[var(--brand-deep-blue)] transition-colors hover:bg-[var(--brand-light-gray)]"
                  >
                    {altLocale.label}
                  </Link>
                );
              })}
            </div>
          ) : null}
        </div>
      </nav>

      <div className="mx-auto grid max-w-[1200px] gap-10 px-4 py-10 md:py-14 lg:grid-cols-[minmax(0,760px)_320px] lg:justify-center">
      <article className="min-w-0">
        <Link
          to={locale.indexRoute as "/blog" | "/es/blog" | "/zh/blog" | "/ur/blog" | "/ar/blog"}
          className="mb-5 inline-flex items-center gap-2 rounded-md border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-[var(--brand-deep-blue)] transition-colors hover:bg-[var(--brand-light-gray)]"
        >
          <ArrowLeft className={`h-4 w-4 ${isRtl ? "rotate-180" : ""}`} aria-hidden="true" />
          {ui.allArticles}
        </Link>
        <div
          className={`flex flex-wrap items-center gap-2 text-xs font-semibold uppercase tracking-wide text-[var(--brand-charcoal)]/60 ${isRtl ? "flex-row-reverse" : ""}`}
        >
          <time suppressHydrationWarning dateTime={article.created_at}>
            {formatDate(article.created_at, locale)}
          </time>
          {minutes > 0 ? (
            <>
              <span aria-hidden="true">·</span>
              <span>{ui.minRead(minutes)}</span>
            </>
          ) : null}
        </div>
        <h1 className="mt-2 font-[Oswald,sans-serif] text-3xl font-bold uppercase leading-[1.12] tracking-tight text-[var(--brand-deep-blue)] md:text-[2.6rem]">
          {article.title}
        </h1>
        {article.excerpt ? (
          <p className="mt-4 text-lg leading-relaxed text-[var(--brand-charcoal)]/75">
            {article.excerpt}
          </p>
        ) : null}

        {article.hero_image_url ? (
          <img
            src={heroSrc(article.hero_image_url, 800)}
            alt={article.title}
            width={760}
            height={428}
            fetchPriority="high"
            decoding="async"
            className="mt-7 aspect-[16/9] w-full rounded-xl object-cover shadow-sm"
          />
        ) : null}

        <ShareButtons url={shareUrl} title={article.title} className="mt-7" />

        {body ? (
          <div
            className="blog-content mt-9"
            // Content comes from our own trusted CMS sync.
            dangerouslySetInnerHTML={{ __html: body }}
          />
        ) : null}

        <div className="mt-10 border-t border-gray-200 pt-6">
          <ShareButtons url={shareUrl} title={article.title} />
        </div>

        {related.length > 0 ? (
          <nav aria-label={ui.moreArticles} className="mt-10 rounded-lg border border-gray-200 bg-[var(--brand-light-gray)] p-5">
            <h2 className="font-[Oswald,sans-serif] text-lg font-bold uppercase text-[var(--brand-deep-blue)]">
              {ui.moreArticles}
            </h2>
            <ul className="mt-3 space-y-2">
              {related.slice(0, 3).map((r) => (
                <li key={r.slug}>
                  <Link
                    to="/blog/$slug"
                    params={{ slug: r.slug }}
                    className="text-sm font-semibold text-[var(--brand-bright-blue)] underline-offset-2 hover:underline"
                  >
                    {r.title}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
        ) : null}

        {locale.code === "en" ? (
          <BlogServiceLinks title={article.title} slug={slug} />
        ) : null}

        <div className="mt-12 rounded-lg bg-[var(--brand-deep-blue)] p-6 text-center text-white md:p-8">
          <h2 className="font-[Oswald,sans-serif] text-2xl font-bold uppercase">
            {ui.needQuote}
          </h2>
          <p className="mt-2 text-white/80">{ui.freeEstimates}</p>
          <div className={`mt-5 flex flex-col justify-center gap-3 sm:flex-row ${isRtl ? "sm:flex-row-reverse" : ""}`}>
            <button
              type="button"
              onClick={() => openQuoteModal()}
              className="rounded-md bg-[var(--brand-red)] px-6 py-3 font-bold text-white"
            >
              {ui.getFreeQuote}
            </button>
            <a
              href="tel:+15189001913"
              className="rounded-md bg-white px-6 py-3 font-bold text-[var(--brand-deep-blue)]"
            >
              <CallLabel label={ui.callLabel} />
            </a>
          </div>
        </div>

        <div className="mt-8 text-center">
          <Link
            to={locale.indexRoute as "/blog" | "/es/blog" | "/zh/blog" | "/ur/blog" | "/ar/blog"}
            className="inline-flex items-center gap-2 rounded-md border border-gray-200 bg-white px-5 py-3 text-sm font-bold text-[var(--brand-deep-blue)] transition-colors hover:bg-[var(--brand-light-gray)]"
          >
            <ArrowLeft className={`h-4 w-4 ${isRtl ? "rotate-180" : ""}`} aria-hidden="true" />
            {ui.backToBlog}
          </Link>
        </div>
      </article>

        <div className="hidden lg:block">
          <div className="sticky top-28 flex max-h-[calc(100vh-9rem)] flex-col">
            <BlogSidebar
              articles={related}
              localeCode={locale.code}
              heading={ui.moreArticles}
              fitViewport
            />
          </div>
        </div>
      </div>

      {related.length > 0 ? (
        <div className="mx-auto max-w-[760px] px-4 pb-12 lg:hidden">
          <BlogSidebar
            articles={related.slice(0, 4)}
            localeCode={locale.code}
            heading={ui.moreArticles}
          />
        </div>
      ) : null}
    </main>
  );
}
